import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, ArrowRight, X } from 'lucide-react';
import AccountTransactionPanel from './AccountTransactionPanel';
import JournalEntryPopup from './JournalEntryPopup';
import { tr } from '../lib/i18nHelpers';
import { compareFinancialValues } from '../lib/financialComparison';

interface AccountLine {
  account_code: string;
  account_name: string;
  amount: number;
}

interface PnlData {
  revenue: number;
  cost: number;
  gross_profit: number;
  /** Operating expenses only (excludes `other_expenses`). */
  expenses: number;
  operating_profit?: number;
  other_income?: number;
  other_expenses?: number;
  net_income: number;
  revenue_accounts?: AccountLine[];
  cost_accounts?: AccountLine[];
  expense_accounts?: AccountLine[];
  other_income_accounts?: AccountLine[];
  other_expense_accounts?: AccountLine[];
  source?: 'journal' | 'bank';
  include_drafts?: boolean;
  period?: { start: string; end: string };
}

type DetailKey =
  | 'revenue'
  | 'cost'
  | 'gross_profit'
  | 'operating_expenses'
  | 'operating_profit'
  | 'other_income'
  | 'other_expenses'
  | 'net_income';

interface PnlFormulaBannerProps {
  data: PnlData;
  /** Same statement one year earlier — renders the comparison column. */
  prior?: PnlData;
  /** Deep link from the dashboard, e.g. `pl_focus=revenue`. */
  focus?: string | null;
  /** Optional deep link for unposted bank transactions inside account details. */
  onOpenBankTransaction?: (bankStatementId: string, transactionId: string) => void;
}

interface Block {
  key: DetailKey;
  label: string;
  value: number;
  accounts: AccountLine[];
  description: string;
  variant?: 'input' | 'subtotal' | 'total';
}


const money = (value: number) => `HKD ${(value || 0).toLocaleString(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})}`;

type SemanticTone = 'income' | 'expense' | 'result';

const SEMANTIC_TONES: Record<DetailKey, SemanticTone> = {
  revenue: 'income',
  cost: 'expense',
  gross_profit: 'result',
  operating_expenses: 'expense',
  operating_profit: 'result',
  other_income: 'income',
  other_expenses: 'expense',
  net_income: 'result',
};

const percentOfRevenue = (value: number, revenue: number) => {
  if (!revenue) return null;
  return `${Math.round((value / revenue) * 1000) / 10}%`;
};

const plainMoney = (value: number) => (value || 0).toLocaleString(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Prior-year cell: prior amount plus ↑/↓ %, coloured by whether the move is
 *  favourable — income/result lines rising are good, expense lines rising are
 *  bad (the same inversion as the financial flash). */
function PriorCell({
  current,
  priorValue,
  semantic,
}: {
  current: number;
  priorValue: number | undefined;
  semantic: SemanticTone;
}) {
  if (priorValue === undefined || priorValue === null) {
    return <span className="whitespace-nowrap text-xs text-muted-foreground">—</span>;
  }
  const comparison = compareFinancialValues(current, priorValue);
  const base = 'whitespace-nowrap font-mono text-xs tabular-nums';
  if (comparison.direction === 'flat' || comparison.direction === 'unavailable' || comparison.percent === null) {
    return <span className={`${base} text-muted-foreground`}>{plainMoney(priorValue)}</span>;
  }
  const arrow = comparison.direction === 'up' ? '↑' : '↓';
  const percent = `${Math.abs(comparison.percent).toFixed(1)}%`;
  const favourable = semantic === 'expense'
    ? comparison.direction === 'down'
    : comparison.direction === 'up';
  return (
    <span className={`${base} ${favourable ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-700 dark:text-red-300'}`}>
      {plainMoney(priorValue)} {arrow} {percent}
    </span>
  );
}

const rowInteraction = (onOpen: () => void) => ({
  onClick: onOpen,
  tabIndex: 0,
  role: 'button' as const,
  onKeyDown: (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onOpen();
    }
  },
});

/** One account line of the statement — drills straight into that account's
 *  transactions (journal entries → full double-entry via the detail dialog). */
function AccountRow({
  account,
  priorAccounts,
  semantic,
  hasPrior,
  onOpen,
}: {
  account: AccountLine;
  priorAccounts?: AccountLine[];
  semantic: SemanticTone;
  hasPrior: boolean;
  onOpen: () => void;
}) {
  const priorLine = priorAccounts?.find((p) => p.account_code === account.account_code);
  return (
    <tr
      {...rowInteraction(onOpen)}
      aria-label={`${account.account_code} ${account.account_name}. ${tr('View breakdown', '查看明細', '查看明细')}`}
      className="cursor-pointer transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
    >
      <td className="py-1.5 pl-9 pr-2 text-[13px]">
        <span className="font-mono text-[11px] text-muted-foreground">{account.account_code}</span>
        <span className="ml-2 text-foreground">{account.account_name}</span>
      </td>
      <td className={`py-1.5 pl-2 pr-3 text-right font-mono text-[13px] tabular-nums ${account.amount < 0 ? 'text-red-900 dark:text-red-200' : 'text-foreground'}`}>
        {plainMoney(account.amount)}
      </td>
      {hasPrior && (
        <td className="py-1.5 px-3 text-right">
          <PriorCell current={account.amount} priorValue={priorLine?.amount} semantic={semantic} />
        </td>
      )}
    </tr>
  );
}

/** Subtotal / key line of the statement. Key lines carry the double rule;
 *  `final` is the net result. Optional `chip` shows the share of sales. */
function TotalRow({
  label,
  prefix,
  value,
  chip,
  variant,
  onOpen,
  hasPrior,
  priorValue,
  semantic,
}: {
  label: string;
  prefix?: string;
  value: number;
  chip?: string | null;
  variant: 'subtotal' | 'keyline' | 'final';
  onOpen: () => void;
  hasPrior: boolean;
  priorValue?: number;
  semantic: SemanticTone;
}) {
  const keyline = variant !== 'subtotal';
  const pad = variant === 'final' ? 'py-2.5' : keyline ? 'py-2' : 'py-1.5';
  return (
    <tr
      {...rowInteraction(onOpen)}
      aria-label={`${prefix ? `${prefix} ` : ''}${label}. ${money(value)}. ${tr('View details', '查看詳情', '查看详情')}`}
      className={`cursor-pointer transition-colors hover:bg-muted/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset ${keyline ? 'border-t-2 border-foreground/70' : 'border-t border-border'}`}
    >
      <td className={`px-3 ${pad} ${variant === 'final' ? 'text-[15px] font-bold' : keyline ? 'text-sm font-bold' : 'text-[13px] font-semibold'} text-foreground`}>
        {prefix && <span className="mr-1 font-medium text-muted-foreground">{prefix}</span>}
        <span>{label}</span>
        {chip && (
          <span className="ml-2 inline-block rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
            {chip}
          </span>
        )}
      </td>
      <td className={`pl-2 pr-3 text-right font-mono tabular-nums ${pad} ${variant === 'final' ? 'text-[15px] font-bold' : 'font-semibold'} ${value < 0 ? 'text-red-900 dark:text-red-200' : 'text-foreground'}`}>
        {plainMoney(value)}
      </td>
      {hasPrior && (
        <td className={`px-3 text-right ${pad}`}>
          <PriorCell current={value} priorValue={priorValue} semantic={semantic} />
        </td>
      )}
    </tr>
  );
}

function DetailDialog({
  detail,
  data,
  initialAccount,
  onClose,
  onOpenBankTransaction,
}: {
  detail: Block;
  data: PnlData;
  /** Opened straight from a statement account row — land on its transactions. */
  initialAccount?: AccountLine | null;
  onClose: () => void;
  onOpenBankTransaction?: (bankStatementId: string, transactionId: string) => void;
}) {
  const [selectedAccount, setSelectedAccount] = useState<AccountLine | null>(initialAccount ?? null);
  const [selectedJournalEntry, setSelectedJournalEntry] = useState<any | null>(null);
  const backToDetail = () => setSelectedAccount(null);
  const backToAccount = () => setSelectedJournalEntry(null);
  const openAccount = (account: AccountLine) => {
    setSelectedJournalEntry(null);
    setSelectedAccount(account);
  };

  // NOTE: no reset-on-key effect — the caller remounts this dialog via its
  // `key` (section + optional account) whenever the entry point changes.

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        if (selectedJournalEntry) backToAccount();
        else if (selectedAccount) backToDetail();
        else onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [selectedJournalEntry, selectedAccount, onClose]);

  const share = percentOfRevenue(detail.value, data.revenue);
  const sortedAccounts = useMemo(
    () => [...detail.accounts].sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount)),
    [detail.accounts],
  );
  const sourceLabel = data.source === 'bank'
    ? tr('Bank estimate', '銀行交易估算', '银行交易估算')
    : tr('Posted journal entries', '已過帳分錄', '已过账分录');

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-0 backdrop-blur-sm sm:items-center sm:p-6"
      onClick={selectedJournalEntry
        ? backToAccount
        : selectedAccount
          ? backToDetail
          : onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="pnl-detail-title"
        className="flex h-[80vh] w-[80vw] max-w-none flex-col overflow-hidden rounded-2xl border bg-card shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-4 border-b bg-muted/20 p-5">
          <div>
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {selectedJournalEntry
                  ? tr('Journal entry detail', '分錄詳情', '分录详情')
                  : tr('Income statement detail', '損益表明細', '损益表明细')}
              </p>
              <h3 id="pnl-detail-title" className="mt-1 text-xl font-bold text-foreground">
                {selectedJournalEntry
                  ? (selectedJournalEntry.entry_number || selectedJournalEntry.entry_id)
                  : selectedAccount
                  ? `${selectedAccount.account_code} – ${selectedAccount.account_name}`
                  : detail.label}
              </h3>
              <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
                {selectedJournalEntry
                  ? selectedJournalEntry.description
                  : selectedAccount
                  ? tr('Account transactions for the selected period.', '所選期間的科目交易。', '所选期间的科目交易。')
                  : detail.description}
            </p>
            {!selectedAccount && data.period && (
              <p className="mt-2 text-xs text-muted-foreground">
                {tr('Period', '期間', '期间')}: {data.period.start} – {data.period.end} · {tr('Source', '來源', '来源')}: {sourceLabel}
              </p>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-2">
            {selectedAccount && (
              <button
                type="button"
                onClick={selectedJournalEntry ? backToAccount : backToDetail}
                className="inline-flex items-center gap-1 rounded-md border bg-background px-3 py-2 text-sm text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                aria-label={selectedJournalEntry
                  ? tr('Back to account transactions', '返回科目交易', '返回科目交易')
                  : `${tr('Back to', '返回', '返回')} ${detail.label}`}
              >
                <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                {tr('Back', '返回', '返回')}
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border bg-background p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              aria-label={tr('Close', '關閉', '关闭')}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {selectedAccount ? (
          selectedJournalEntry ? (
            <JournalEntryPopup
              embedded
              entry={selectedJournalEntry}
              onClose={backToAccount}
            />
          ) : (
            <div className="flex-1 overflow-y-auto">
              <AccountTransactionPanel
              accountCode={selectedAccount.account_code}
              accountName={selectedAccount.account_name}
              startDate={data.period?.start || ''}
              endDate={data.period?.end || ''}
              includeDrafts={data.include_drafts}
              onClose={backToDetail}
              onPostClick={onOpenBankTransaction || (() => {})}
              onSelectEntry={setSelectedJournalEntry}
            />
            </div>
          )
        ) : (
          <div className="flex-1 space-y-5 overflow-y-auto p-5">
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="rounded-xl border bg-background p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">{tr('Amount', '金額', '金额')}</p>
                <p className="mt-1 text-xl font-mono font-semibold text-foreground">{money(detail.value)}</p>
              </div>
              <div className="rounded-xl border bg-background p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">{tr('Linked accounts', '關聯科目', '关联科目')}</p>
                <p className="mt-1 text-xl font-semibold text-foreground">{detail.accounts.length}</p>
              </div>
              <div className="rounded-xl border bg-background p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">{tr('Share of revenue', '佔收入', '占收入')}</p>
                <p className="mt-1 text-xl font-semibold text-foreground">{share ? `${share}` : '—'}</p>
              </div>
            </div>

          <div>
                <div className="mb-3 flex items-center justify-between gap-3">
                  <h4 className="text-sm font-semibold text-foreground">
                    {tr('Account breakdown', '科目明細', '科目明细')}
                  </h4>
                  {sortedAccounts.length > 0 && (
                    <span className="text-xs text-muted-foreground">
                      {tr('Press View breakdown for transactions', '按「查看明細」查看交易', '按「查看明细」查看交易')}
                    </span>
                  )}
                </div>

                {sortedAccounts.length === 0 ? (
                  <div className="rounded-xl border border-dashed bg-background p-6 text-center">
                    <p className="text-sm font-medium text-foreground">
                      {tr('No linked accounts', '沒有關聯科目', '没有关联科目')}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      {tr('This value may come from uncategorized bank activity or an empty accounting period.', '此數值可能來自未分類銀行活動或空會計期間。', '此数值可能来自未分类银行活动或空会计期间。')}
                    </p>
                  </div>
                ) : (
                  <div className="overflow-hidden rounded-xl border bg-background">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/30 text-xs text-muted-foreground">
                        <tr>
                          <th className="px-4 py-2 text-left font-medium">{tr('Code', '編號', '编号')}</th>
                          <th className="px-4 py-2 text-left font-medium">{tr('Account', '科目', '科目')}</th>
                          <th className="px-4 py-2 text-right font-medium">{tr('Amount', '金額', '金额')}</th>
                          <th className="px-4 py-2 text-right font-medium">{tr('Share', '佔比', '占比')}</th>
                          <th className="px-4 py-2 text-right font-medium"><span className="sr-only">{tr('Actions', '操作', '操作')}</span></th>
                        </tr>
                      </thead>
                      <tbody>
                        {sortedAccounts.map((account) => {
                          const rowShare = percentOfRevenue(account.amount, data.revenue);
                          return (
                            <tr
                              key={`${account.account_code}-${account.account_name}`}
                              tabIndex={0}
                              role="button"
                              aria-label={`${account.account_code} ${account.account_name}. ${tr('View account transactions', '查看科目交易', '查看科目交易')}`}
                              onClick={() => openAccount(account)}
                              onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                  event.preventDefault();
                                  openAccount(account);
                                }
                              }}
                              className="cursor-pointer border-t border-border/60 transition-colors hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
                            >
                              <td className="px-4 py-3 font-mono text-xs text-muted-foreground">{account.account_code}</td>
                              <td className="px-4 py-3 text-foreground">{account.account_name}</td>
                              <td className={`px-4 py-3 text-right font-mono ${account.amount < 0 ? 'text-red-900 dark:text-red-200' : 'text-foreground'}`}>
                                {money(account.amount)}
                              </td>
                              <td className="px-4 py-3 text-right text-muted-foreground">{rowShare || '—'}</td>
                              <td className="px-4 py-3 text-right">
                                <button
                                  type="button"
                                  onClick={(event) => {
                                    event.stopPropagation();
                                    openAccount(account);
                                  }}
                                  className="inline-flex items-center gap-1 rounded-md border bg-background px-2.5 py-1.5 text-xs font-medium text-primary transition-colors hover:bg-primary hover:text-primary-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                  aria-label={`${account.account_code} ${account.account_name}. ${tr('View breakdown', '查看明細', '查看明细')}`}
                                >
                                  {tr('View breakdown', '查看明細', '查看明细')}
                                  <ArrowRight className="h-3 w-3" aria-hidden="true" />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function PnlFormulaBanner({
  data,
  prior,
  focus,
  onOpenBankTransaction,
}: PnlFormulaBannerProps) {
  const operatingProfit = data.operating_profit ?? ((data.gross_profit || 0) - (data.expenses || 0));
  const otherIncome = data.other_income || 0;
  const otherExpenses = data.other_expenses || 0;

  const blocks: Record<DetailKey, Block> = {
    revenue: {
      key: 'revenue',
      label: tr('Revenue', '收入', '收入'),
      value: data.revenue || 0,
      accounts: data.revenue_accounts || [],
      description: tr(
        'Operating revenue earned in the selected period before direct costs and operating expenses.',
        '選定期間內已賺取的營業收入，未扣除直接成本及營運支出。',
        '选定期间内已赚取的营业收入，未扣除直接成本及营运支出。',
      ),
    },
    cost: {
      key: 'cost',
      label: tr('Direct Costs', '直接成本', '直接成本'),
      value: data.cost || 0,
      accounts: data.cost_accounts || [],
      description: tr(
        'Costs directly attributable to delivering the goods or services that generated revenue.',
        '與提供產生收入的商品或服務直接相關的成本。',
        '与提供产生收入的商品或服务直接相关的成本。',
      ),
    },
    gross_profit: {
      key: 'gross_profit',
      label: tr('Gross Profit', '毛利', '毛利'),
      value: data.gross_profit || 0,
      accounts: [...(data.revenue_accounts || []), ...(data.cost_accounts || [])],
      description: tr(
        'Trading result after deducting direct costs from operating revenue.',
        '營業收入扣除直接成本後的貿易成果。',
        '营业收入扣除直接成本后的贸易成果。',
      ),
      variant: 'subtotal',
    },
    operating_expenses: {
      key: 'operating_expenses',
      label: tr('Operating Expenses', '營運支出', '营运支出'),
      value: data.expenses || 0,
      accounts: data.expense_accounts || [],
      description: tr(
        'Running costs excluded from direct costs, such as salaries, rent, admin, and depreciation.',
        '未計入直接成本的營運開支，例如薪金、租金、行政費及折舊。',
        '未计入直接成本的营运开支，例如薪金、租金、行政费及折旧。',
      ),
    },
    operating_profit: {
      key: 'operating_profit',
      label: tr('Operating Profit', '營業利潤', '营业利润'),
      value: operatingProfit || 0,
      accounts: [...(data.revenue_accounts || []), ...(data.cost_accounts || []), ...(data.expense_accounts || [])],
      description: tr(
        'Profit from core operations before other income and other expenses.',
        '扣除其他收入及其他支出前的核心業務利潤。',
        '扣除其他收入及其他支出前的核心业务利润。',
      ),
      variant: 'subtotal',
    },
    other_income: {
      key: 'other_income',
      label: tr('Other Income', '其他收入', '其他收入'),
      value: otherIncome,
      accounts: data.other_income_accounts || [],
      description: tr(
        'Income outside normal trading operations, such as interest or one-off recoveries.',
        '正常業務以外的收入，例如利息或一次性回收。',
        '正常业务以外的收入，例如利息或一次性回收。',
      ),
    },
    other_expenses: {
      key: 'other_expenses',
      label: tr('Other Expenses', '其他支出', '其他支出'),
      value: otherExpenses,
      accounts: data.other_expense_accounts || [],
      description: tr(
        'Non-operating costs outside the core trading result.',
        '核心業務成果以外的非營運支出。',
        '核心业务成果以外的非营运支出。',
      ),
    },
    net_income: {
      key: 'net_income',
      label: tr('Net Profit', '淨利', '淨利'),
      value: data.net_income || 0,
      accounts: [
        ...(data.revenue_accounts || []),
        ...(data.cost_accounts || []),
        ...(data.expense_accounts || []),
        ...(data.other_income_accounts || []),
        ...(data.other_expense_accounts || []),
      ],
      description: tr(
        'Final result after direct costs, operating expenses, other income, and other expenses.',
        '扣除直接成本、營運支出、其他收入及其他支出後的最終成果。',
        '扣除直接成本、营运支出、其他收入及其他支出后的最终成果。',
      ),
      variant: 'total',
    },
  };

  const [activeDetail, setActiveDetail] = useState<DetailKey | null>(null);
  // Account row clicked on the statement → open the detail dialog straight at
  // that account's transactions (skipping the section's account list).
  const [drillAccount, setDrillAccount] = useState<AccountLine | null>(null);

  useEffect(() => {
    if (focus === 'revenue') setActiveDetail('revenue');
    else if (focus === 'expense') setActiveDetail('operating_expenses');
    else if (focus === 'net') setActiveDetail('net_income');
  }, [focus]);

  const openDetail = (key: DetailKey) => {
    setDrillAccount(null);
    setActiveDetail(key);
  };
  const openAccountDrill = (key: DetailKey, account: AccountLine) => {
    setActiveDetail(key);
    setDrillAccount(account);
  };
  const closeDetail = () => {
    setActiveDetail(null);
    setDrillAccount(null);
  };
  const activeBlock = activeDetail ? blocks[activeDetail] : null;

  // Prior-year figures for the comparison column, matched by section.
  const priorOperatingProfit = prior?.operating_profit ?? ((prior?.gross_profit || 0) - (prior?.expenses || 0));
  const priorByKey: Partial<Record<DetailKey, number>> | null = prior ? {
    revenue: prior.revenue,
    cost: prior.cost,
    gross_profit: prior.gross_profit,
    operating_expenses: prior.expenses,
    operating_profit: priorOperatingProfit,
    other_income: prior.other_income || 0,
    other_expenses: prior.other_expenses || 0,
    net_income: prior.net_income,
  } : null;
  const priorAccountsByKey: Partial<Record<DetailKey, AccountLine[]>> | null = prior ? {
    revenue: prior.revenue_accounts,
    cost: prior.cost_accounts,
    operating_expenses: prior.expense_accounts,
    other_income: prior.other_income_accounts,
    other_expenses: prior.other_expense_accounts,
  } : null;
  const hasPrior = !!prior;
  const percentChip = (value: number) => {
    const share = percentOfRevenue(value, data.revenue);
    return share ? `${share} ${tr('of sales', '佔銷售', '占销售')}` : null;
  };

  // One statement section: header + sorted account rows + subtotal. Sections
  // with at most one account collapse to a single line (other income/expenses).
  const renderSection = (key: DetailKey, prefix?: string, forceInline = false) => {
    const block = blocks[key];
    const semantic = SEMANTIC_TONES[key];
    const rows = [...block.accounts].sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount));
    if (forceInline && rows.length <= 1) {
      return (
        <TotalRow
          key={key}
          label={block.label}
          prefix={prefix}
          value={block.value}
          variant="subtotal"
          onOpen={() => openDetail(key)}
          hasPrior={hasPrior}
          priorValue={priorByKey?.[key]}
          semantic={semantic}
        />
      );
    }
    return (
      <React.Fragment key={key}>
        <tr
          {...rowInteraction(() => openDetail(key))}
          aria-label={`${block.label}. ${tr('View details', '查看詳情', '查看详情')}`}
          className="cursor-pointer transition-colors hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-inset"
        >
          <td className="px-3 pb-1 pt-4 text-[13px] font-semibold text-foreground">
            {prefix && <span className="mr-1 font-medium text-muted-foreground">{prefix}</span>}
            {block.label}
          </td>
          <td className="px-3 pb-1 pt-4 text-right text-[11px] text-muted-foreground">
            {rows.length} {tr('accounts', '個科目', '个科目')}
          </td>
          {hasPrior && <td className="px-3 pt-4" />}
        </tr>
        {rows.map((account) => (
          <AccountRow
            key={`${key}-${account.account_code}`}
            account={account}
            priorAccounts={priorAccountsByKey?.[key]}
            semantic={semantic}
            hasPrior={hasPrior}
            onOpen={() => openAccountDrill(key, account)}
          />
        ))}
        <TotalRow
          label={`${block.label} — ${tr('total', '合計', '合计')}`}
          value={block.value}
          variant="subtotal"
          onOpen={() => openDetail(key)}
          hasPrior={hasPrior}
          priorValue={priorByKey?.[key]}
          semantic={semantic}
        />
      </React.Fragment>
    );
  };

  return (
    <section
      aria-labelledby="pnl-formula-heading"
      className="rounded-2xl border bg-card p-4 shadow-sm sm:p-5"
    >
      <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h3 id="pnl-formula-heading" className="text-base font-semibold text-foreground">
            {tr('Income statement', '損益表', '损益表')}
          </h3>
          <p className="mt-1 text-xs text-muted-foreground">
            {tr(
              'Click any line to open its breakdown — account rows drill through to journal entries.',
              '點擊任何一行開啟明細——科目行可再深入至日誌分錄。',
              '点击任何一行开启明细——科目行可再深入至日志分录。',
            )}
          </p>
        </div>
        {data.period && (
          <p className="whitespace-nowrap text-xs text-muted-foreground">
            {data.period.start} – {data.period.end}
            {hasPrior ? ` · ${tr('vs prior year', '與上年比較', '与上年比较')}` : ''}
          </p>
        )}
      </div>

      {/* Vertical statement (2026-10-30 client review: transpose the formula).
          "Less:/Add:" prefixes replace the old − / = operator chips — the
          formula reads top-down: Revenue − Cost = GP − OpEx = OP + Other
          income − Other expenses = Net. */}
      <div className="mt-4 overflow-x-auto">
        <table className="w-full min-w-[420px] text-sm">
          <tbody>
            {renderSection('revenue')}
            {renderSection('cost', tr('Less:', '減：', '减：'))}
            <TotalRow
              label={blocks.gross_profit.label}
              value={blocks.gross_profit.value}
              chip={percentChip(blocks.gross_profit.value)}
              variant="keyline"
              onOpen={() => openDetail('gross_profit')}
              hasPrior={hasPrior}
              priorValue={priorByKey?.gross_profit}
              semantic="result"
            />
            {renderSection('operating_expenses', tr('Less:', '減：', '减：'))}
            <TotalRow
              label={blocks.operating_profit.label}
              value={blocks.operating_profit.value}
              variant="keyline"
              onOpen={() => openDetail('operating_profit')}
              hasPrior={hasPrior}
              priorValue={priorByKey?.operating_profit}
              semantic="result"
            />
            {renderSection('other_income', tr('Add:', '加：', '加：'), true)}
            {renderSection('other_expenses', tr('Less:', '減：', '减：'), true)}
            <TotalRow
              label={blocks.net_income.label}
              value={blocks.net_income.value}
              chip={percentChip(blocks.net_income.value)}
              variant="final"
              onOpen={() => openDetail('net_income')}
              hasPrior={hasPrior}
              priorValue={priorByKey?.net_income}
              semantic="result"
            />
          </tbody>
        </table>
      </div>

      {activeBlock && (
        <DetailDialog
          key={`${activeDetail}-${drillAccount?.account_code || 'section'}`}
          detail={activeBlock}
          data={data}
          initialAccount={drillAccount}
          onClose={closeDetail}
          onOpenBankTransaction={onOpenBankTransaction}
        />
      )}
    </section>
  );
}
